/*
 * ELASTICSEARCH CONFIDENTIAL
 * __________________
 *
 *  Copyright Elasticsearch B.V. All rights reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Elasticsearch B.V. and its suppliers, if any.
 * The intellectual and technical concepts contained herein
 * are proprietary to Elasticsearch B.V. and its suppliers and
 * may be covered by U.S. and Foreign Patents, patents in
 * process, and are protected by trade secret or copyright
 * law.  Dissemination of this information or reproduction of
 * this material is strictly forbidden unless prior written
 * permission is obtained from Elasticsearch B.V.
 */

import React, { Component, Fragment } from 'react'
import { clone, size } from 'lodash'

import {
  EuiBadge,
  EuiCallOut,
  EuiFlexGroup,
  EuiSpacer,
  EuiText,
  EuiTextColor,
  EuiTitle,
} from '@elastic/eui'

import { FormattedMessage } from '@modules/i18n'
import type { DeploymentTemplateRequestBody } from '@modules/cloud-api/v1/types'

import { getDataTopologies } from '@/lib/deploymentTemplates/getTopologiesFromTemplate'
import { hasIlmNodeAttributes } from '@/lib/stackDeployments/indexManagement'

import NodeAttributeForm, { pillColorDefinitions } from './NodeAttributeForm'

type Props = {
  template: DeploymentTemplateRequestBody
  updateDeploymentTemplate: (deploymentTemplate: DeploymentTemplateRequestBody) => void
}

type State = {
  pillColors: any
}

class IndexLifecycleManagementSettings extends Component<Props, State> {
  state: State = this.getInitialState()

  getInitialState() {
    const { template } = this.props

    const dataInstances = getDataTopologies({ deployment: template.deployment_template })

    const pillColors = {}

    dataInstances.forEach((topologyElement) => {
      const nodeAttributes = topologyElement.elasticsearch?.node_attributes

      if (!nodeAttributes) {
        return
      }

      Object.keys(nodeAttributes).forEach((key) => {
        const label = `${key}:${nodeAttributes[key]}`

        if (!pillColors[label]) {
          pillColors[label] = pillColorDefinitions[size(pillColors) % pillColorDefinitions.length]
        }
      })
    })

    return { pillColors }
  }

  render() {
    const { template, updateDeploymentTemplate } = this.props
    const { pillColors } = this.state

    const dataInstances = getDataTopologies({ deployment: template.deployment_template })
    const nodeAttributesExistForAllInstances = hasIlmNodeAttributes(dataInstances)

    return (
      <div>
        <EuiTitle size='s'>
          <EuiFlexGroup component='h4' gutterSize='s' alignItems='center'>
            <FormattedMessage
              id='index-lifecycle-management-settings.title'
              defaultMessage='Index lifecycle management (ILM)'
            />
            <EuiBadge color='accent'>
              <FormattedMessage
                id='index-lifecycle-management-settings.recommended'
                defaultMessage='Recommended'
              />
            </EuiBadge>
          </EuiFlexGroup>
        </EuiTitle>
        <EuiSpacer size='s' />
        <EuiTitle size='xxxs' textTransform='uppercase'>
          <h5>
            <EuiTextColor color='subdued'>
              <FormattedMessage
                id='index-lifecycle-management-settings.stack-version'
                defaultMessage='Stack version 6.7 and later'
              />
            </EuiTextColor>
          </h5>
        </EuiTitle>
        <EuiSpacer size='m' />
        <EuiText>
          <p>
            <FormattedMessage
              id='index-lifecycle-management-settings.further-description'
              defaultMessage='Move indices between different stages of the lifecycle (hot, warm, cold, delete) in response to index details and other factors, such as shard size and performance requirements.'
            />
          </p>
        </EuiText>

        {!nodeAttributesExistForAllInstances && (
          <Fragment>
            <EuiSpacer />
            <EuiCallOut
              color='warning'
              title={
                <FormattedMessage
                  id='index-lifecycle-management-settings.missing-config.title'
                  defaultMessage='Missing configuration'
                />
              }
            >
              <FormattedMessage
                id='index-lifecycle-management-settings.missing-config.description'
                defaultMessage='Add one or more node attributes for each data node to enable ILM.'
              />
            </EuiCallOut>
          </Fragment>
        )}

        <EuiSpacer />

        <EuiTitle size='xs'>
          <h5>
            <FormattedMessage
              id='index-lifecycle-management-settings.node-attributes.title'
              defaultMessage='Node attributes'
            />
          </h5>
        </EuiTitle>
        <EuiSpacer size='xs' />
        <EuiText>
          <p>
            <FormattedMessage
              id='index-lifecycle-management-settings.node-attributes.description'
              defaultMessage='Assign attributes to each node - hot, warm or cold - to configure index lifecycle policies in Kibana.'
            />
          </p>
        </EuiText>
        <EuiSpacer size='m' />
        {dataInstances.map((instance, index) => (
          <NodeAttributeForm
            template={template}
            key={index}
            instance={instance}
            updateDeploymentTemplate={updateDeploymentTemplate}
            addPillColor={this.addPillColor}
            pillColors={pillColors}
          />
        ))}
      </div>
    )
  }

  addPillColor = (option) => {
    const { pillColors } = this.state
    const newPillColors = clone(pillColors)
    newPillColors[option.label] = option.color

    this.setState({ pillColors: newPillColors })
  }
}

export default IndexLifecycleManagementSettings
